// Binary wire protocol, version 1. Little-endian. Positions are u16 in quarter pixels (the hub map
// is 12,704 px wide, so x*4 fits). Never trust anything the client sends: the server re-checks speed,
// cooldowns and range (MMO-PLAN.md section 8).

export const PROTO = 9;   // 9: facing is 8 world directions, cars steer to a screen direction
export const PROTO_PREV = 8;   // 8: vehicles, goods/containers/crafting (C2S.Use, S2C.Goods), looks, car gauge
// prev 7:   // 7: iso world (Map carries seed+depth+gen), S2C.World destruction deltas
// prev 6:   // 6: progression (Stats/Bag/Loot/Fx), skills, items, dialog, quests, panels; hp on the wire = fraction of max
// prev 5:   // 5: arcade sign-in (sso:/s: tokens), Account + Pick (character select)
// prev 4:   // 4: weather kind + temperature in Snap
// prev:   // 2: maps (rooms), Enter, S2C.Map; 3: dungeon instances (Map carries seed+theme), coins

export const enum C2S { Hello = 1, Input = 2, Chat = 3, Ping = 4, Attack = 5, Enter = 6, Pick = 7, Skill = 8, Talk = 9, Item = 10, Learn = 11, Choose = 12, Use = 13, Dodge = 14, Mutes = 15 }   // 15: u8 n, n x str public id: who this listener has muted (R7-010 round 3)
export const enum S2C { Welcome = 1, Snap = 2, Chat = 4, Pong = 5, Event = 6, Kick = 7, Map = 8, Account = 9, Stats = 10, Bag = 11, Loot = 12, Fx = 13, Dialog = 14, Quests = 15, Panel = 16, World = 17, Goods = 18, Look = 19, Car = 20, Story = 21, Hearing = 22 }
// S2C.Story (studio 008, chapter 1): u8 flags (1 your party holds a dusk pass, 2 start the 4 s wait-till-dusk fade now);
// u8 n, n x str district key that is set (008-hearing.md 2.4: its props and lights show); u8 n, n x stand ring (pos x,
// pos y, u8 radius px, u8 fill 0-255, str label); u8 n, n x str journal line, newest first; u32 your escort's entity id (0: none);
// u8 the impossible door as YOU see it (0 a shut door, 'NOT A DOOR'; 1 it opens for you: str invitation, str obligation
// follow, the sign turning from one to the other as your escort nears; 2 an empty frame, the hearing behind you)
// S2C.Hearing (studio 008 phase 2, the records room: 008-hearing.md 1), sent while you are in an echo and on change:
// u8 mode (0 none, 1 HEARING, 2 HOSTILE, 3 HOSTILE in 2 s: the bells, 4 held: Anika is down, 5 resolved);
// u8 hearing count (1 = the first); u8 lit lecterns, u8 submitted (bit i = lectern i in ECHO spots order: consent,
// delivery, destination); u8 3 x (str label, str claim); u8 cabinet glows; u8 n plates x (u8 seal index, u8 fill 0-255:
// 255 just lit, tightening to 0 as it binds); u8 you are bound; u8 the projection reads (0 page lowered, 1 lifted)

// Map ids: 0 is the street; rooms are numbered from 1 in manifest venue order (server-assigned).
export const WORLD = 0;
export const DUNGEON_BASE = 1000;   // map ids >= this are dungeon instances
export const FLOOR_BASE = 500;      // map ids FLOOR_BASE+1 .. are upper floors of buildings (world.json floors[id-1]); the floor system, 2026-10-15
export const isFloorMap = (m: number) => m > FLOOR_BASE && m < DUNGEON_BASE;

export const enum Kind { Player = 0, Zombie = 1, Npc = 2, Proj = 3, Boss = 4, Ally = 5, Vehicle = 6 }
export const enum Flag { Moving = 1, Attacking = 2, Hurt = 4, Dead = 8, InCar = 16, Down = 32, Crouch = 64, Sprint = 128 }   // Down: knocked flat (Zomboid); Crouch/Sprint: how a player moves

export interface EntState { id: number; kind: number; sub: number; x: number; y: number; dir: number; flags: number; hp: number }

const enc = new TextEncoder();
const dec = new TextDecoder();

export class W {
  buf: ArrayBuffer; v: DataView; o = 0;
  constructor(n = 256) { this.buf = new ArrayBuffer(n); this.v = new DataView(this.buf); }
  ensure(n: number) {
    if (this.o + n <= this.buf.byteLength) return;
    const nb = new ArrayBuffer(Math.max(this.buf.byteLength * 2, this.o + n));
    new Uint8Array(nb).set(new Uint8Array(this.buf));
    this.buf = nb; this.v = new DataView(nb);
  }
  u8(x: number) { this.ensure(1); this.v.setUint8(this.o, x); this.o += 1; return this; }
  i8(x: number) { this.ensure(1); this.v.setInt8(this.o, x); this.o += 1; return this; }
  u16(x: number) { this.ensure(2); this.v.setUint16(this.o, x, true); this.o += 2; return this; }
  u32(x: number) { this.ensure(4); this.v.setUint32(this.o, x, true); this.o += 4; return this; }
  f64(x: number) { this.ensure(8); this.v.setFloat64(this.o, x, true); this.o += 8; return this; }
  pos(x: number) { return this.u16(Math.max(0, Math.min(65535, Math.round(x * 4)))); }
  str(s: string, max = 200) {
    const b = enc.encode(s.slice(0, max));
    this.u8(Math.min(255, b.length));
    this.ensure(b.length);
    new Uint8Array(this.buf, this.o, Math.min(255, b.length)).set(b.subarray(0, 255));
    this.o += Math.min(255, b.length);
    return this;
  }
  done(): Uint8Array { return new Uint8Array(this.buf, 0, this.o); }
}

export class R {
  v: DataView; o = 0;
  constructor(public b: ArrayBuffer | Uint8Array) {
    this.v = b instanceof Uint8Array ? new DataView(b.buffer, b.byteOffset, b.byteLength) : new DataView(b);
  }
  get left() { return this.v.byteLength - this.o; }
  u8() { const x = this.v.getUint8(this.o); this.o += 1; return x; }
  i8() { const x = this.v.getInt8(this.o); this.o += 1; return x; }
  u16() { const x = this.v.getUint16(this.o, true); this.o += 2; return x; }
  u32() { const x = this.v.getUint32(this.o, true); this.o += 4; return x; }
  f64() { const x = this.v.getFloat64(this.o, true); this.o += 8; return x; }
  pos() { return this.u16() / 4; }
  str() {
    const n = this.u8();
    const s = dec.decode(new Uint8Array(this.v.buffer, this.v.byteOffset + this.o, n));
    this.o += n;
    return s;
  }
}

// ---- message builders shared by tests and both ends
export function hello(name: string, token = ''): Uint8Array {
  return new W(64).u8(C2S.Hello).u8(PROTO).str(name, 20).str(token, 128).done();
}
/** mods: Move.Sprint | Move.Crouch (an optional trailing byte; older bots send none) */
export function input(seq: number, dx: number, dy: number, mods = 0): Uint8Array {
  return new W(8).u8(C2S.Input).u16(seq & 0xffff).i8(Math.round(dx * 100)).i8(Math.round(dy * 100)).u8(mods).done();
}
export const enum Move { Sprint = 1, Crouch = 2 }
/** walking speed multipliers, shared so the client predicts what the server does (Zomboid: run, walk, sneak) */
export const SPRINT_MULT = 1.45, CROUCH_MULT = 0.55;

export function writeEnt(w: W, e: EntState) {
  w.u32(e.id).u8(e.kind).u8(e.sub).pos(e.x).pos(e.y).u8(e.dir).u8(e.flags).u8(Math.max(0, Math.min(255, e.hp)));
}
export function readEnt(r: R): EntState {
  return { id: r.u32(), kind: r.u8(), sub: r.u8(), x: r.pos(), y: r.pos(), dir: r.u8(), flags: r.u8(), hp: r.u8() };
}

// Names: what a player may call themselves (the arcade's nick rules are stricter; this is the floor).
export function cleanName(s: string): string {
  return s.replace(/[^A-Za-z0-9 _.\-']/g, '').trim().slice(0, 16) || 'Survivor';
}
export function cleanChat(s: string): string {
  // control, format (bidi overrides, zero-width) and line/paragraph separators are dropped (R6-008)
  return s.replace(/[\u0000-\u001f\u007f\p{Cf}\u2028\u2029]/gu, '').trim().slice(0, 160);
}

// close codes the client acts on
export const CLOSE_SIGNIN = 4001;     // no valid session: send the player to the arcade sign-in
export const CLOSE_REPLACED = 4002;   // the same account connected somewhere else

// item operations (C2S.Item op)
export const enum ItemOp { Equip = 1, Unequip = 2, Salvage = 3, Forge = 4, Enchant = 5, Drop = 6, EnchantPick = 7, Pickup = 8, Identify = 9 }   // Identify: uid 0 = everything; arg 1 = use a Receipt
// effect kinds (S2C.Fx)
export const enum FxKind { Damage = 1, Telegraph = 2, Cone = 3, Heal = 4, LevelUp = 5, Text = 6, Line = 7, Break = 8, SkillHit = 9 }   // SkillHit: a skill landed on something; b = the caster's roster index (its burst plays at x, y)   // Break: a = 1 when it gave way, text = 'glass'|'wood'|'metal'|'brick'
/** C2S.Attack and C2S.Skill may end with a u8 aim: the world angle in 256ths of a turn (0 = east, 64 = south) */
export const aimByte = (dx: number, dy: number) => ((Math.round(Math.atan2(dy, dx) / (Math.PI * 2) * 256) % 256) + 256) % 256;

// C2S.Use ops (survival, Zomboid-style)
export const enum UseOp { Use = 1, Craft = 2, Take = 3, Alt = 4, Drop = 5, Close = 6, TakeAll = 7, Throw = 8, Buy = 9, Sell = 10, Gamble = 11, Shop = 12, Treat = 13 }   // 9-12: Seamus's shop (a = good id, '#uid' for gear, or the gamble slot); Treat: a = the good, n = body part 0-5 (255: the worst wound it helps)
